import { z } from 'zod'

import type { BillingAuthorizationStatus } from './billing-authorization'
import type {
  AuthorizationAmountPolicy,
  BillingAuthorizationType,
} from './entity-billing-profile'
import { BillingAuthorizationStatusSchema } from './billing-authorization'
import {
  AuthorizationAmountPolicySchema,
  BillingAuthorizationTypeSchema,
} from './entity-billing-profile'

// Vendor contracts & billing (SD-124): the client Contracts & Billing model
// inverted — vendors bill the organization. Money is integer cents
// (Quotes precedent); dates are date-only `YYYY-MM-DD` strings on the wire.
// See docs/plans/vendor-contracts-billing.md.

/** Largest cents value a Postgres INTEGER column holds ($21,474,836.47). */
export const SUPPLIER_BILLING_MAX_CENTS = 2_147_483_647

export const SupplierContractStatusSchema = z.enum([
  'DRAFT',
  'ACTIVE',
  'EXPIRED',
  'TERMINATED',
])
export type SupplierContractStatus = z.infer<
  typeof SupplierContractStatusSchema
>

export const SupplierContractStatusLabels: Record<
  SupplierContractStatus,
  string
> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  EXPIRED: 'Expired',
  TERMINATED: 'Terminated',
}

export const SupplierBillStatusSchema = z.enum([
  'RECEIVED',
  'APPROVED',
  'PAID',
  'DISPUTED',
  'VOID',
])
export type SupplierBillStatus = z.infer<typeof SupplierBillStatusSchema>

export const SupplierBillStatusLabels: Record<SupplierBillStatus, string> = {
  RECEIVED: 'Received',
  APPROVED: 'Approved',
  PAID: 'Paid',
  DISPUTED: 'Disputed',
  VOID: 'Void',
}

/**
 * Authorization statuses a user may set by hand. EXHAUSTED and EXPIRED are
 * derived on read from bills and dates (same rule as client authorizations).
 */
export const SUPPLIER_AUTHORIZATION_MANUAL_STATUSES = [
  'DRAFT',
  'ACTIVE',
  'CANCELLED',
  'ARCHIVED',
] as const satisfies readonly BillingAuthorizationStatus[]

export const SupplierAuthorizationManualStatusSchema = z.enum(
  SUPPLIER_AUTHORIZATION_MANUAL_STATUSES,
)

const DateOnlySchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a date in YYYY-MM-DD format')

const CentsSchema = z
  .number()
  .int('Amounts are whole cents')
  .min(0)
  .max(SUPPLIER_BILLING_MAX_CENTS)

const CurrencySchema = z
  .string()
  .trim()
  .length(3)
  .transform(value => value.toUpperCase())

const OptionalText = (max?: number) =>
  (max ? z.string().trim().max(max) : z.string().trim())
    .nullable()
    .optional()
    .transform(value => (value === '' ? null : value))

const OptionalUuid = z
  .string()
  .uuid()
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null))

const OptionalUrl = z
  .string()
  .trim()
  .url('Invalid document URL')
  .max(500)
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null))

// ---------------------------------------------------------------------------
// Rollups
// ---------------------------------------------------------------------------

/** Spend totals for a contract / authorization / supplier, all in cents. */
export interface SupplierSpendRollup {
  /** Cap in cents; null = uncapped. */
  authorizedCents: number | null
  /** RECEIVED + DISPUTED bills (not yet spend). */
  pendingCents: number
  approvedCents: number
  paidCents: number
  /** Actual spend = approved + paid. */
  consumedCents: number
  /** authorized - consumed (may go negative under WARNING_ONLY); null when uncapped. */
  remainingCents: number | null
  /** Whole percent of the cap consumed; null when uncapped or cap is 0. */
  percentUsed: number | null
  billCount: number
}

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export interface SupplierContract {
  id: string
  organizationId: string
  supplierId: string
  contractNumber: string | null
  title: string
  status: SupplierContractStatus
  startDate: string | null
  endDate: string | null
  currency: string
  authorizedAmountCents: number | null
  paymentTermId: string | null
  paymentTermName: string | null
  documentUrl: string | null
  notes: string | null
  ownerId: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
  rollup: SupplierSpendRollup
}

export const CreateSupplierContractSchema = z.object({
  supplierId: z.string().uuid(),
  contractNumber: OptionalText(100),
  title: z.string().trim().min(1, 'Title is required').max(255),
  status: SupplierContractStatusSchema.default('DRAFT'),
  startDate: DateOnlySchema.nullable().optional(),
  endDate: DateOnlySchema.nullable().optional(),
  currency: CurrencySchema.default('USD'),
  authorizedAmountCents: CentsSchema.nullable().optional(),
  paymentTermId: OptionalUuid,
  documentUrl: OptionalUrl,
  notes: OptionalText(),
  ownerId: OptionalUuid,
})
export type CreateSupplierContract = z.input<
  typeof CreateSupplierContractSchema
>
export type CreateSupplierContractParsed = z.output<
  typeof CreateSupplierContractSchema
>

export const UpdateSupplierContractSchema = CreateSupplierContractSchema.omit({
  supplierId: true,
})
  .partial()
  .extend({
    // `.partial()` keeps `.default()` values, so re-declare the defaulted
    // fields: an omitted status/currency must mean "unchanged".
    status: SupplierContractStatusSchema.optional(),
    currency: CurrencySchema.optional(),
  })
export type UpdateSupplierContract = z.input<
  typeof UpdateSupplierContractSchema
>
export type UpdateSupplierContractParsed = z.output<
  typeof UpdateSupplierContractSchema
>

// ---------------------------------------------------------------------------
// Spend authorizations
// ---------------------------------------------------------------------------

export interface SupplierAuthorization {
  id: string
  organizationId: string
  supplierId: string
  contractId: string | null
  authorizationType: BillingAuthorizationType
  referenceNumber: string | null
  label: string
  /** Effective status (EXHAUSTED/EXPIRED derived on read). */
  status: BillingAuthorizationStatus
  /** The stored, manually set status. */
  manualStatus: BillingAuthorizationStatus
  authorizedAmountCents: number | null
  currency: string
  effectiveDate: string | null
  expirationDate: string | null
  documentUrl: string | null
  notes: string | null
  ownerId: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
  rollup: SupplierSpendRollup
}

export const CreateSupplierAuthorizationSchema = z.object({
  supplierId: z.string().uuid(),
  contractId: OptionalUuid,
  authorizationType: BillingAuthorizationTypeSchema,
  referenceNumber: OptionalText(120),
  label: z.string().trim().min(1, 'Label is required').max(255),
  status: SupplierAuthorizationManualStatusSchema.default('ACTIVE'),
  authorizedAmountCents: CentsSchema.nullable().optional(),
  currency: CurrencySchema.default('USD'),
  effectiveDate: DateOnlySchema.nullable().optional(),
  expirationDate: DateOnlySchema.nullable().optional(),
  documentUrl: OptionalUrl,
  notes: OptionalText(),
  ownerId: OptionalUuid,
})
export type CreateSupplierAuthorization = z.input<
  typeof CreateSupplierAuthorizationSchema
>
export type CreateSupplierAuthorizationParsed = z.output<
  typeof CreateSupplierAuthorizationSchema
>

export const UpdateSupplierAuthorizationSchema =
  CreateSupplierAuthorizationSchema.omit({ supplierId: true })
    .partial()
    .extend({
      status: SupplierAuthorizationManualStatusSchema.optional(),
      currency: CurrencySchema.optional(),
    })
export type UpdateSupplierAuthorization = z.input<
  typeof UpdateSupplierAuthorizationSchema
>
export type UpdateSupplierAuthorizationParsed = z.output<
  typeof UpdateSupplierAuthorizationSchema
>

// ---------------------------------------------------------------------------
// Bills
// ---------------------------------------------------------------------------

export interface SupplierBill {
  id: string
  organizationId: string
  supplierId: string
  supplierName: string | null
  contractId: string | null
  contractTitle: string | null
  supplierAuthorizationId: string | null
  authorizationLabel: string | null
  purchaseOrderId: string | null
  purchaseOrderNumber: string | null
  billNumber: string
  billDate: string
  dueDate: string | null
  currency: string
  subtotalCents: number
  taxCents: number
  totalCents: number
  status: SupplierBillStatus
  approvedById: string | null
  approvedAt: string | null
  paidAt: string | null
  paymentReference: string | null
  disputeReason: string | null
  voidedAt: string | null
  notes: string | null
  createdById: string | null
  createdAt: string
  updatedAt: string
}

export const CreateSupplierBillSchema = z.object({
  supplierId: z.string().uuid(),
  contractId: OptionalUuid,
  supplierAuthorizationId: OptionalUuid,
  purchaseOrderId: OptionalUuid,
  billNumber: z.string().trim().min(1, 'Bill number is required').max(100),
  billDate: DateOnlySchema,
  dueDate: DateOnlySchema.nullable().optional(),
  currency: CurrencySchema.default('USD'),
  subtotalCents: CentsSchema,
  taxCents: CentsSchema.default(0),
  notes: OptionalText(),
})
export type CreateSupplierBill = z.input<typeof CreateSupplierBillSchema>
export type CreateSupplierBillParsed = z.output<typeof CreateSupplierBillSchema>

export const UpdateSupplierBillSchema = CreateSupplierBillSchema.omit({
  supplierId: true,
})
  .partial()
  .extend({
    currency: CurrencySchema.optional(),
    taxCents: CentsSchema.optional(),
  })
export type UpdateSupplierBill = z.input<typeof UpdateSupplierBillSchema>
export type UpdateSupplierBillParsed = z.output<typeof UpdateSupplierBillSchema>

export const PaySupplierBillSchema = z.object({
  paidAt: DateOnlySchema.nullable().optional(),
  paymentReference: OptionalText(255),
})
export type PaySupplierBill = z.input<typeof PaySupplierBillSchema>

export const SupplierBillReasonSchema = z.object({
  reason: OptionalText(2000),
})
export type SupplierBillReason = z.input<typeof SupplierBillReasonSchema>

export const SupplierBillListQuerySchema = z.object({
  supplierId: z.string().uuid().optional(),
  contractId: z.string().uuid().optional(),
  authorizationId: z.string().uuid().optional(),
  status: z
    .string()
    .optional()
    .transform(value =>
      value
        ? value
            .split(',')
            .map(part => part.trim())
            .filter(Boolean)
        : undefined,
    )
    .pipe(z.array(SupplierBillStatusSchema).optional()),
  from: DateOnlySchema.optional(),
  to: DateOnlySchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(100),
})
export type SupplierBillListQuery = z.output<typeof SupplierBillListQuerySchema>

export interface SupplierBillsResponse {
  data: SupplierBill[]
  meta: {
    page: number
    limit: number
    totalItems: number
    totalPages: number
  }
}

export type SupplierBillingWarningCode =
  | 'SUPPLIER_SPEND_CAP_EXCEEDED'
  | 'SUPPLIER_AUTHORIZATION_OUT_OF_WINDOW'

export interface SupplierBillingWarning {
  code: SupplierBillingWarningCode
  message: string
  /** Cents over the cap (cap warnings only). */
  overByCents?: number
}

export interface SupplierBillTransitionResult {
  bill: SupplierBill
  /** Cap warnings under WARNING_ONLY; empty otherwise. */
  warnings: SupplierBillingWarning[]
}

export const SupplierSpendSummaryQuerySchema = z.object({
  from: DateOnlySchema.optional(),
  to: DateOnlySchema.optional(),
  supplierId: z.string().uuid().optional(),
})
export type SupplierSpendSummaryQuery = z.output<
  typeof SupplierSpendSummaryQuerySchema
>

export interface SupplierSpendSummaryCurrencyRow {
  currency: string
  approvedCents: number
  paidCents: number
  /** approved + paid */
  consumedCents: number
  pendingCents: number
  billCount: number
}

/** Actual vendor spend (APPROVED + PAID bills) by bill date, per currency. */
export interface SupplierSpendSummary {
  from: string | null
  to: string | null
  byCurrency: SupplierSpendSummaryCurrencyRow[]
}

export interface SupplierBillingSummary {
  supplierId: string
  authorizationAmountPolicy: AuthorizationAmountPolicy
  currency: string
  spend: SupplierSpendSummary
  contractCount: number
  authorizationCount: number
  /** APPROVED bills whose due date has passed. */
  overdueCount: number
}

export const UpdateSupplierBillingSettingsSchema = z.object({
  authorizationAmountPolicy: AuthorizationAmountPolicySchema,
})
export type UpdateSupplierBillingSettings = z.infer<
  typeof UpdateSupplierBillingSettingsSchema
>
